import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Space, Typography, Spin, Alert, message, Tag } from 'antd';
import { SaveOutlined, CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import axiosClient from '@/api/axiosClient';
import { useAuth } from '@/auth/useAuth';

const { Text } = Typography;

interface SubmissionData {
  id?: string;
  confirmationNumber?: string;
  submissionMethod?: string;
  notes?: string;
  submittedBy?: string;
  submittedAt?: string;
}

interface SubmissionDetailsFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const SubmissionDetailsForm: React.FC<SubmissionDetailsFormProps> = ({
  opportunityId,
  onComplete,
}) => {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(['ENTERPRISE_ARCHITECT', 'BD_MANAGER', 'ADMIN', 'CEO']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<SubmissionData | null>(null);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [submissionMethod, setSubmissionMethod] = useState('');
  const [notes, setNotes] = useState('');

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/opportunities/${opportunityId}/submission`);
      if (response.data) {
        setExisting(response.data);
        setConfirmationNumber(response.data.confirmationNumber || '');
        setSubmissionMethod(response.data.submissionMethod || '');
        setNotes(response.data.notes || '');
      }
    } catch {
      // no submission yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmission();
  }, [opportunityId]);

  const handleSave = async () => {
    if (!confirmationNumber.trim()) {
      message.warning('Confirmation number is required');
      return;
    }
    setSaving(true);
    try {
      await axiosClient.post(`/opportunities/${opportunityId}/submission`, {
        confirmationNumber: confirmationNumber.trim(),
        submissionMethod: submissionMethod.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      message.success('Submission details saved');
      await fetchSubmission();
      onComplete();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save submission details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading submission details...
      </Card>
    );
  }

  const isComplete = Boolean(existing?.confirmationNumber);

  if (isComplete) {
    return (
      <Card
        size="small"
        title={<Text strong style={{ fontSize: 14 }}>Phase G: Submission Record</Text>}
        style={{ marginTop: 12, borderColor: 'var(--color-success)', background: 'var(--color-success-light)' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={4}>
          <Space>
            <Tag icon={<CheckCircleOutlined />} color="success">Submitted</Tag>
            {existing!.submittedBy && (
              <Text type="secondary">by {existing!.submittedBy}</Text>
            )}
          </Space>
          <Text><Text type="secondary">Confirmation #:</Text> <Text strong>{existing!.confirmationNumber}</Text></Text>
          {existing!.submissionMethod && (
            <Text><Text type="secondary">Method:</Text> {existing!.submissionMethod}</Text>
          )}
          {existing!.notes && (
            <Text><Text type="secondary">Notes:</Text> {existing!.notes}</Text>
          )}
        </Space>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 14 }}>Phase G: Submission Details</Text>}
      style={{ marginTop: 12, borderColor: 'var(--color-warning)' }}
    >
      {!canEdit && (
        <Alert
          message={<span><LockOutlined /> Read-only — only the Enterprise Architect, BD Manager, Admin, or CEO can record submission.</span>}
          type="info"
          showIcon={false}
          style={{ marginBottom: 12 }}
        />
      )}
      <Alert
        message="Submission confirmation number is required to advance to Phase H"
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Confirmation Number <span style={{ color: 'var(--color-error)' }}>*</span>
          </Text>
          <Input
            value={confirmationNumber}
            onChange={(e) => setConfirmationNumber(e.target.value)}
            placeholder="e.g. SAM-2024-001234"
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Submission Method</Text>
          <Input
            value={submissionMethod}
            onChange={(e) => setSubmissionMethod(e.target.value)}
            placeholder="e.g. SAM.gov, Email, FedConnect"
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Notes</Text>
          <Input.TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about the submission..."
            rows={2}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!canEdit || !confirmationNumber.trim()}
          >
            Record Submission
          </Button>
        </div>
      </Space>
    </Card>
  );
};

export default SubmissionDetailsForm;
